import { PackageOpen, Sparkles } from "lucide-react";
import { useGetIngredientsQuery } from "@/redux/services/inventory/ingredient.api";
import { Ingredient } from "@/redux/services/inventory/inventory.type";
import { cn } from "@/lib/utils";
import { reportCard } from "./reportCard";

interface RestockSectionProps {
  restaurantId: string;
}

function RestockSection({ restaurantId }: RestockSectionProps) {
  const { data, isLoading, isFetching } = useGetIngredientsQuery({
    restaurantId,
    query: { lowStock: true, limit: 50 },
  });

  const lowStock = data?.results ?? [];

  return (
    <section className={cn(reportCard, "p-6")}>
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          À réapprovisionner
        </h2>
      </div>

      {isLoading || isFetching ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 rounded-lg bg-muted/40 animate-pulse"
            />
          ))}
        </div>
      ) : lowStock.length === 0 ? (
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          <PackageOpen className="h-5 w-5" />
          <span>
            Tous les ingrédients sont au-dessus du seuil minimum.
          </span>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3">
            {lowStock.length} ingrédient{lowStock.length > 1 ? "s" : ""} sous le
            seuil minimum à recommander.
          </p>
          <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
            {lowStock.map((ingredient) => (
              <li
                key={ingredient.id}
                className="px-4 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-card-foreground truncate">
                    {ingredient.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatStock(ingredient)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function formatStock(ingredient: Ingredient): string {
  const current = Number(ingredient.currentStock);
  const threshold = Number(ingredient.lowStockThreshold);
  const unit = ingredient.baseUnit;
  const fmt = (n: number) =>
    Number.isFinite(n)
      ? n.toLocaleString("fr-FR", { maximumFractionDigits: 2 })
      : "—";
  return `${fmt(current)} ${unit} disponibles · seuil ${fmt(threshold)} ${unit}`;
}

export default RestockSection;
